import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { getAssignments, getAssignmentShareLink } from "../api/assignments";
import type { AssignmentResponse } from "../api/types";
import {
    exportAssignmentAsA4Image,
    exportAssignmentsAsA4Pdf,
    openAssignmentA4Image,
    prefersImageInNewTab
} from "../assignments/exportAssignmentImages";
import { AssignmentMapModal } from "../assignments/AssignmentMapModal";
import {
    useDistributionRounds,
    useOpenAssignmentFlyers,
    useOpenDistributionResponses,
    useCreateDistributionRounds,
    useCloseDistributionRound,
    useOpenAssignmentMaterials,
    useSetAssignmentFlyer,
    useSetAssignmentMaterialReceived,
    type DistributionRound
} from "../api/distributionRounds";
import { fetchFlyerTypes, useCampaigns } from "../api/campaigns";
import {
    materialOptionShortLabel,
    normalizeMaterialOption,
    type MaterialOption
} from "../api/materialOption";
import { useKretsMembers, type KretsMember } from "../api/kretsMembers";
import { useMunicipalities } from "../api/map";
import { useSelectedMunicipalityId } from "./selectedMunicipality";
import { DistributionRoundsDialog, type NewRoundDraft } from "./DistributionRoundsDialog";
import {
    ASSIGNMENT_SORT_LABELS,
    assignmentComparer,
    readAssignmentSortBy,
    storeAssignmentSortBy,
    type AssignmentSortBy
} from "../assignments/assignmentSort";
import "./admin-assignments-table-page.css";

/**
 * Förslag på namn när nya utdelningar startas: innevarande och nästa månad, t.ex.
 * "Utdelning augusti 2026" och "Utdelning september 2026". De läggs upp tillsammans så att
 * utdelarna kan säga ja till den ena, den andra eller båda i samma formulär.
 */
const suggestRoundNames = (): string[] => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const label = (date: Date) =>
        `Utdelning ${date.toLocaleDateString("sv-SE", { month: "long" })} ${date.getFullYear()}`;
    return [label(now), label(next)];
};

const canDeliverLabel = (canDeliver?: boolean | null): string =>
    canDeliver === true ? "Ja" : canDeliver === false ? "Nej" : "–";

/**
 * Radens status sett från den som kör ut materialet: vad kan jag göra med den här raden nu?
 * Statusen väger ihop alla öppna utdelningar — någon som fått augusti men inte september är
 * "delvis utlämnat" och ska fortfarande synas bland dem som väntar på material.
 */
type RowStatus = "delivered" | "partial" | "ready" | "declined" | "incomplete" | "noAnswer";

/**
 * Uppdragets läge i en enskild omgång, som det visas i sin kolumn. Svaret (kan dela) hör till
 * utdelaren och upprepas därför på personens alla rader, medan kvitterat material hör till
 * det enskilda uppdraget — kansliet delar ut ett område i taget.
 */
type RoundCell = {
    roundId: string;
    canDeliver?: boolean | null;
    materialReceived: boolean;
};

const rowStatus = (cells: RoundCell[]): RowStatus => {
    const answered = cells.filter((cell) => cell.canDeliver != null);
    if (answered.length === 0) return "noAnswer";
    // Läggs en ny utdelning till efter att någon svarat ska den svarslösa omgången jagas —
    // annars ser en person som svarat på augusti ut att vara klar även för september.
    if (answered.length < cells.length) return "incomplete";

    const accepted = cells.filter((cell) => cell.canDeliver === true);
    if (accepted.length === 0) return "declined";

    const received = accepted.filter((cell) => cell.materialReceived).length;
    if (received === accepted.length) return "delivered";
    return received > 0 ? "partial" : "ready";
};

const STATUS_LABELS: Record<RowStatus, string> = {
    delivered: "Material utlämnat",
    partial: "Material delvis utlämnat",
    ready: "Kan köras ut nu",
    declined: "Kan inte dela ut",
    incomplete: "Svar saknas för någon utdelning",
    noAnswer: "Inget svar"
};

/**
 * Listan körs i tre steg: jaga svar → dela ut material → klart. Därför en tabell per steg
 * i stället för en enda lång, så det syns direkt hur mycket som återstår i varje steg.
 */
type SectionKey = "noAnswer" | "answered" | "delivered";

const sectionOf = (status: RowStatus): SectionKey =>
    status === "noAnswer" || status === "incomplete"
        ? "noAnswer"
        : status === "delivered"
          ? "delivered"
          : "answered";

const SECTION_ORDER: { key: SectionKey; title: string; empty: string }[] = [
    { key: "noAnswer", title: "Har inte svarat", empty: "Alla har svarat på alla utdelningar." },
    {
        key: "answered",
        title: "Har svarat – material ej utlämnat",
        empty: "Inga uppdrag väntar på material."
    },
    { key: "delivered", title: "Har fått material", empty: "Inget material utlämnat än." }
];

/** En färdigtuggad rad: uppdraget plus utdelarens uppgifter och svar för de öppna omgångarna. */
type TableRow = {
    assignment: AssignmentResponse;
    member?: KretsMember;
    distributorName: string;
    cells: RoundCell[];
    materialOption: MaterialOption | null;
    status: RowStatus;
};

export const AdminAssignmentsTablePage = (): JSX.Element => {
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useSelectedMunicipalityId();

    const municipalitiesQuery = useMunicipalities({ limit: 2000, includeGeometry: false }, null, { enabled: true });

    const municipalities = useMemo(
        () =>
            (municipalitiesQuery.data?.features ?? [])
                .map((feature) => ({
                    id: String(feature.id ?? feature.properties.municipalityPolygonId),
                    name: feature.properties.name
                }))
                .sort((a, b) => a.name.localeCompare(b.name, "sv")),
        [municipalitiesQuery.data?.features]
    );

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["assignments", selectedMunicipalityId],
        queryFn: () => getAssignments(undefined, undefined, selectedMunicipalityId),
        enabled: !!selectedMunicipalityId,
        staleTime: 60_000,
        retry: 2
    });

    // Adress och telefon hör till utdelaren (kretsmedlemmen), inte till uppdraget.
    const { data: kretsMembers = [] } = useKretsMembers();
    const kretsMemberById = useMemo(
        () => new Map(kretsMembers.map((member) => [member.id, member])),
        [kretsMembers]
    );

    // Ibland kör man efter område, ibland person för person — därför båda vyerna.
    // Valet delas med Uppdragsöverblick, så listorna ligger i samma ordning.
    const [groupBy, setGroupBy] = useState<AssignmentSortBy>(() => readAssignmentSortBy());

    const getDistributorName = (assignment: AssignmentResponse): string =>
        (assignment.kretsMemberId ? kretsMemberById.get(assignment.kretsMemberId)?.name : undefined) ??
        assignment.kretsMemberName ??
        assignment.distributorName ??
        "";

    const assignments = useMemo(
        () =>
            [...(data ?? [])].sort(
                assignmentComparer(groupBy, (assignment) => assignment.name, getDistributorName)
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, groupBy, kretsMemberById]
    );

    // Svaren (kan dela + materialval) hör till utdelaren och en öppen utdelningsomgång.
    // Flera omgångar kan vara öppna samtidigt — en kolumn per omgång i tabellen.
    const roundsQuery = useDistributionRounds(selectedMunicipalityId);
    const openRounds = useMemo(() => roundsQuery.data?.open ?? [], [roundsQuery.data?.open]);
    const responsesQuery = useOpenDistributionResponses(selectedMunicipalityId);
    const createRoundsMutation = useCreateDistributionRounds();
    const closeRoundMutation = useCloseDistributionRound();
    const materialMutation = useSetAssignmentMaterialReceived();

    // Vilket flygblad varje uppdrag ska dela ut i varje omgång. Valet sitter per uppdrag och
    // omgång — utdelarna delar olika flygblad beroende på var de bor.
    const assignmentFlyersQuery = useOpenAssignmentFlyers(selectedMunicipalityId);
    const assignmentFlyerMutation = useSetAssignmentFlyer();

    // Kvitterat material per uppdrag och omgång. Kansliet har tio områden att dela på och tar
    // dem ett i taget, så krysset gäller raden — inte utdelarens alla uppdrag.
    const assignmentMaterialsQuery = useOpenAssignmentMaterials(selectedMunicipalityId);

    const materialReceivedKeys = useMemo(
        () =>
            new Set(
                (assignmentMaterialsQuery.data ?? []).map(
                    (receipt) => `${receipt.distributionRoundId}:${receipt.assignmentId}`
                )
            ),
        [assignmentMaterialsQuery.data]
    );

    const flyerByKey = useMemo(
        () =>
            new Map(
                (assignmentFlyersQuery.data ?? []).map((choice) => [
                    `${choice.distributionRoundId}:${choice.assignmentId}`,
                    choice
                ])
            ),
        [assignmentFlyersQuery.data]
    );

    // Flygbladen att välja bland: har omgången en kampanj är det den kampanjens flygblad,
    // annars alla kampanjers (grupperade per kampanj i listan).
    const { data: campaigns = [] } = useCampaigns();

    const campaignIdsForRound = useMemo(() => {
        const fallback = campaigns.filter((campaign) => !campaign.isArchived).map((c) => c.id);
        return new Map(
            openRounds.map((round) => [round.id, round.campaignId ? [round.campaignId] : fallback])
        );
    }, [campaigns, openRounds]);

    const campaignNameById = useMemo(
        () => new Map(campaigns.map((campaign) => [campaign.id, campaign.name])),
        [campaigns]
    );

    const flyerCampaignIds = useMemo(
        () => [...new Set([...campaignIdsForRound.values()].flat())],
        [campaignIdsForRound]
    );

    const flyerQueries = useQueries({
        queries: flyerCampaignIds.map((campaignId) => ({
            queryKey: ["flyerTypes", campaignId],
            queryFn: () => fetchFlyerTypes(campaignId),
            staleTime: 5 * 60 * 1000
        }))
    });

    /** Flygbladen per kampanj, med samma namnupplösning som kampanjvyn använder. */
    const flyersByCampaign = useMemo(() => {
        const byCampaign = new Map<string, { id: string; name: string }[]>();
        flyerCampaignIds.forEach((campaignId, index) => {
            const flyers = flyerQueries[index]?.data ?? [];
            byCampaign.set(
                campaignId,
                flyers.map((flyer) => ({
                    id: flyer.id,
                    name: flyer.flyerType?.name || flyer.name || "Okänt flygblad"
                }))
            );
        });
        return byCampaign;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flyerCampaignIds, flyerQueries.map((query) => query.dataUpdatedAt).join("|")]);

    const responseByKey = useMemo(
        () =>
            new Map(
                (responsesQuery.data ?? []).map((response) => [
                    `${response.distributionRoundId}:${response.kretsMemberId}`,
                    response
                ])
            ),
        [responsesQuery.data]
    );

    const sections = useMemo(() => {
        const rowsBySection: Record<SectionKey, TableRow[]> = { noAnswer: [], answered: [], delivered: [] };

        for (const assignment of assignments) {
            const member = assignment.kretsMemberId ? kretsMemberById.get(assignment.kretsMemberId) : undefined;

            // Svaret hör till utdelaren, så det upprepas på personens alla rader. Kvitterat
            // material hör däremot till uppdraget — raderna kryssas i var för sig.
            const cells: RoundCell[] = openRounds.map((round) => {
                const response = assignment.kretsMemberId
                    ? responseByKey.get(`${round.id}:${assignment.kretsMemberId}`)
                    : undefined;
                return {
                    roundId: round.id,
                    canDeliver: response?.canDeliver,
                    materialReceived: materialReceivedKeys.has(`${round.id}:${assignment.id}`)
                };
            });

            const materialOption = assignment.kretsMemberId
                ? openRounds
                      .map((round) =>
                          normalizeMaterialOption(
                              responseByKey.get(`${round.id}:${assignment.kretsMemberId}`)?.materialOption
                          )
                      )
                      .find((option) => option !== null) ?? null
                : null;

            const status = rowStatus(cells);

            rowsBySection[sectionOf(status)].push({
                assignment,
                member,
                distributorName:
                    member?.name ?? assignment.kretsMemberName ?? assignment.distributorName ?? "",
                cells,
                materialOption,
                status
            });
        }

        return SECTION_ORDER.map((section) => ({
            ...section,
            rows: rowsBySection[section.key],
            totalTargetAddresses: rowsBySection[section.key].reduce(
                (sum, row) => sum + (row.assignment.targetAddressCount ?? 0),
                0
            )
        }));
    }, [assignments, kretsMemberById, materialReceivedKeys, openRounds, responseByKey]);

    // Kvitteringen sparas per uppdrag och omgång, så det är den kombinationen som spinner.
    const [savingMaterialKey, setSavingMaterialKey] = useState<string | null>(null);
    const [exportingId, setExportingId] = useState<string | null>(null);
    // Flygbladsvalet sparas per uppdrag och omgång — den kombinationen spinner medan det sparas.
    const [savingFlyerKey, setSavingFlyerKey] = useState<string | null>(null);
    // Hela gruppens kartor i en PDF – en A4-sida per uppdrag, att skriva ut i en klump.
    const [exportingSectionKey, setExportingSectionKey] = useState<SectionKey | null>(null);
    // Sätts bara under själva utskriften: då göms de andra grupperna på papperet.
    const [printSectionKey, setPrintSectionKey] = useState<SectionKey | null>(null);
    // Två lägen: zoombar karta på skärmen, eller nedladdad A4-bild att ta med.
    const [mapAssignment, setMapAssignment] = useState<AssignmentResponse | null>(null);
    const [isRoundsDialogOpen, setIsRoundsDialogOpen] = useState(false);

    const municipalityName = municipalities.find((m) => m.id === selectedMunicipalityId)?.name ?? "";

    const toggleMaterialReceived = (roundId: string, assignmentId: string, materialReceived: boolean) => {
        const key = `${roundId}:${assignmentId}`;
        setSavingMaterialKey(key);
        materialMutation.mutate(
            { roundId, assignmentId, materialReceived },
            {
                onSuccess: () => {
                    // Utlämnat material sätter även "kan dela = ja" på utdelaren, så svaren
                    // måste hämtas om för att kolumnen ska stämma direkt.
                    assignmentMaterialsQuery.refetch();
                    responsesQuery.refetch();
                    roundsQuery.refetch();
                },
                onError: () => toast.error("Kunde inte spara ändringen."),
                onSettled: () => setSavingMaterialKey(null)
            }
        );
    };

    /** Nya utdelningar; redan öppna lämnas öppna om man inte kryssat i att avsluta dem. */
    const handleCreateRounds = (rounds: NewRoundDraft[], closeOpenRounds: boolean) => {
        if (!selectedMunicipalityId) return;

        createRoundsMutation.mutate(
            {
                municipalityPolygonId: selectedMunicipalityId,
                rounds: rounds.map((round) => ({
                    name: round.name,
                    campaignId: round.campaignId || undefined
                })),
                closeOpenRounds
            },
            {
                onSuccess: (created) => {
                    setIsRoundsDialogOpen(false);
                    responsesQuery.refetch();
                    toast.success(
                        created.length === 1
                            ? "Utdelningen är startad. Utdelarna kan svara nu."
                            : `${created.length} utdelningar är startade. Utdelarna kan svara nu.`
                    );
                },
                onError: () => toast.error("Kunde inte starta utdelningen.")
            }
        );
    };

    const handleCloseRound = (round: DistributionRound) => {
        if (
            !window.confirm(
                `Avsluta "${round.name}"? Svaren sparas, men utdelarna kan inte svara på den längre.`
            )
        ) {
            return;
        }

        closeRoundMutation.mutate(round.id, {
            onSuccess: () => {
                responsesQuery.refetch();
                toast.success(`"${round.name}" är avslutad.`);
            },
            onError: () => toast.error("Kunde inte avsluta utdelningen.")
        });
    };

    const handleDownloadMap = async (assignment: (typeof assignments)[number]) => {
        setExportingId(assignment.id);
        try {
            // På telefonen öppnas bilden i en ny flik – en nedladdning hamnar bara i galleriet.
            const showImage = prefersImageInNewTab() ? openAssignmentA4Image : exportAssignmentAsA4Image;
            await showImage(assignment);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Kunde inte hämta kartan.");
        } finally {
            setExportingId(null);
        }
    };

    /** Flygbladet sparas per uppdrag och omgång; tom markering nollställer valet. */
    const handleFlyerChange = (roundId: string, assignmentId: string, campaignFlyerId: string) => {
        const key = `${roundId}:${assignmentId}`;
        setSavingFlyerKey(key);
        assignmentFlyerMutation.mutate(
            { roundId, assignmentId, campaignFlyerId: campaignFlyerId || null },
            {
                onError: () => toast.error("Kunde inte spara flygbladet."),
                onSettled: () => setSavingFlyerKey(null)
            }
        );
    };

    /**
     * Utskrift av en enskild grupp: klassen som gömmer de andra måste sitta i DOM:en innan
     * dialogen öppnas, så utskriften startas i en effekt efter att valet har renderats.
     */
    useEffect(() => {
        if (!printSectionKey) return;

        let cancelled = false;
        const done = () => {
            if (!cancelled) setPrintSectionKey(null);
        };

        window.addEventListener("afterprint", done, { once: true });
        window.print();
        // Alla webbläsare skickar inte afterprint – släpp filtret ändå så sidan inte fastnar.
        const timer = window.setTimeout(done, 1000);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            window.removeEventListener("afterprint", done);
        };
    }, [printSectionKey]);

    /**
     * Alla kartor i en grupp på en gång: en PDF med en A4-sida per uppdrag, i samma ordning
     * som tabellen. Uppdrag utan ritad yta har ingen karta att rita och hoppas över.
     */
    const handleExportSectionMaps = async (section: { key: SectionKey; title: string; rows: TableRow[] }) => {
        const withArea = section.rows.map((row) => row.assignment).filter((assignment) => assignment.area);
        if (withArea.length === 0) {
            toast.error("Inget uppdrag i gruppen har en ritad yta – ingen PDF skapades.");
            return;
        }

        const toastId = `assignments-section-export-${section.key}`;
        setExportingSectionKey(section.key);
        try {
            const result = await exportAssignmentsAsA4Pdf(
                withArea,
                ({ current, total, name }) =>
                    toast.loading(`Skapar PDF ${current}/${total}: ${name}`, { id: toastId }),
                { fileNamePrefix: `${municipalityName} ${section.title}` }
            );
            toast.success(
                `Klart: PDF med ${result.exported} kartor${result.skipped > 0 ? `, ${result.skipped} hoppades över` : ""}.`,
                { id: toastId }
            );
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Kunde inte skapa PDF:en.", { id: toastId });
        } finally {
            setExportingSectionKey(null);
        }
    };

    /**
     * Flygbladet för ett uppdrag i en omgång. På papperet skrivs namnet ut som text — listan
     * används när materialet lämnas ut, och då är det flygbladet som ska stämma.
     */
    const renderFlyerPicker = (roundId: string, assignmentId: string): JSX.Element => {
        const choice = flyerByKey.get(`${roundId}:${assignmentId}`);
        const campaignIds = campaignIdsForRound.get(roundId) ?? [];
        const groups = campaignIds
            .map((campaignId) => ({
                campaignId,
                campaignName: campaignNameById.get(campaignId) ?? "Kampanj",
                flyers: flyersByCampaign.get(campaignId) ?? []
            }))
            .filter((group) => group.flyers.length > 0);

        // Ett valt flygblad ur en annan (t.ex. arkiverad) kampanj måste finnas som alternativ,
        // annars skulle <select> visa fel rad och en oavsiktlig ändring sparas vid nästa klick.
        const hasSelected =
            !choice || groups.some((group) => group.flyers.some((f) => f.id === choice.campaignFlyerId));

        const options =
            groups.length === 1
                ? groups[0].flyers.map((flyer) => (
                      <option key={flyer.id} value={flyer.id}>
                          {flyer.name}
                      </option>
                  ))
                : groups.map((group) => (
                      <optgroup key={group.campaignId} label={group.campaignName}>
                          {group.flyers.map((flyer) => (
                              <option key={flyer.id} value={flyer.id}>
                                  {flyer.name}
                              </option>
                          ))}
                      </optgroup>
                  ));

        return (
            <>
                <select
                    className="assignments-table__flyer-select assignments-table__screen-only"
                    aria-label="Flygblad"
                    title="Flygbladet som uppdraget delar ut i den här utdelningen"
                    value={choice?.campaignFlyerId ?? ""}
                    disabled={savingFlyerKey === `${roundId}:${assignmentId}` || groups.length === 0}
                    onChange={(event) => handleFlyerChange(roundId, assignmentId, event.target.value)}
                >
                    <option value="">
                        {groups.length === 0 ? "Inga flygblad" : "Inget flygblad"}
                    </option>
                    {!hasSelected && choice && (
                        <option value={choice.campaignFlyerId}>{choice.flyerName}</option>
                    )}
                    {options}
                </select>
                <span className="assignments-table__print-only assignments-table__flyer-print">
                    {choice?.flyerName ?? "–"}
                </span>
            </>
        );
    };

    const renderRow = ({ assignment, member, distributorName, cells, materialOption, status }: TableRow) => {
        // Kvitteringen sparas per uppdrag, så raden spinner medan någon av dess rutor sparas.
        const isSaving = cells.some((cell) => savingMaterialKey === `${cell.roundId}:${assignment.id}`);

        return (
        <tr
            key={assignment.id}
            className={`assignments-table__row assignments-table__row--${status}${
                isSaving ? " assignments-table__row--saving" : ""
            }`}
            title={STATUS_LABELS[status]}
        >
            <td>
                {groupBy === "distributor" ? (
                    <>
                        <span className="assignments-table__name">
                            {distributorName || "Ingen utdelare"}
                        </span>
                        <span className="assignments-table__distributor">{assignment.name}</span>
                    </>
                ) : (
                    <>
                        <span className="assignments-table__name">{assignment.name}</span>
                        {distributorName && (
                            <span className="assignments-table__distributor">{distributorName}</span>
                        )}
                    </>
                )}
            </td>
            <td className="assignments-table__num">{assignment.targetAddressCount ?? "–"}</td>
            <td>{member?.homeAddress?.trim() || "–"}</td>
            <td className="assignments-table__phone">{member?.phoneNumber?.trim() || "–"}</td>
            <td className="assignments-table__material-option">
                {materialOptionShortLabel(materialOption) ?? "–"}
            </td>
            {cells.map((cell) => {
                const cellStatus = cell.materialReceived
                    ? "delivered"
                    : cell.canDeliver === true
                      ? "ready"
                      : cell.canDeliver === false
                        ? "declined"
                        : "noAnswer";

                // Har utdelaren tackat nej till omgången finns inget material att lämna ut och
                // inget flygblad att välja — då ska cellen bara visa nejet.
                if (cellStatus === "declined") {
                    return (
                        <td key={cell.roundId} className="assignments-table__round-cell">
                            <span className="assignments-table__chip assignments-table__chip--declined">
                                {canDeliverLabel(cell.canDeliver)}
                            </span>
                        </td>
                    );
                }

                return (
                    <td key={cell.roundId} className="assignments-table__round-cell">
                        <span className={`assignments-table__chip assignments-table__chip--${cellStatus}`}>
                            {canDeliverLabel(cell.canDeliver)}
                        </span>
                        {assignment.kretsMemberId ? (
                            <label className="assignments-table__material-check">
                                <input
                                    type="checkbox"
                                    className="assignments-table__checkbox assignments-table__screen-only"
                                    checked={cell.materialReceived}
                                    disabled={isSaving}
                                    onChange={(event) =>
                                        toggleMaterialReceived(
                                            cell.roundId,
                                            assignment.id,
                                            event.target.checked
                                        )
                                    }
                                />
                                <span className="assignments-table__print-only assignments-table__check-print">
                                    {cell.materialReceived ? "✓" : "☐"}
                                </span>
                                <span className="assignments-table__material-label">material</span>
                            </label>
                        ) : (
                            <span className="assignments-table__material-label">–</span>
                        )}
                        {renderFlyerPicker(cell.roundId, assignment.id)}
                    </td>
                );
            })}
            <td className="assignments-table__screen-only-cell assignments-table__row-actions">
                <Link
                    to={`/admin/assignments?edit=${assignment.id}`}
                    className="assignments-table__edit-link"
                    title="Öppna uppdraget i kartvyn för redigering"
                >
                    Redigera
                </Link>
                {assignment.area && (
                    <>
                        <button
                            type="button"
                            className="assignments-table__map-button"
                            onClick={() => setMapAssignment(assignment)}
                            title="Visa uppdraget på en zoombar karta"
                        >
                            Visa karta
                        </button>
                        <button
                            type="button"
                            className="assignments-table__map-button"
                            disabled={exportingId === assignment.id}
                            onClick={() => handleDownloadMap(assignment)}
                            title="Ladda ned A4-karta för det här uppdraget"
                        >
                            {exportingId === assignment.id ? "Hämtar…" : "Exportera karta"}
                        </button>
                    </>
                )}
            </td>
        </tr>
        );
    };

    // Länken är öppen och skyddas bara av sin token, som hämtas (och skapas vid behov) här.
    const [isCopyingLink, setIsCopyingLink] = useState(false);

    const handleCopyShareLink = async () => {
        if (!selectedMunicipalityId) return;

        setIsCopyingLink(true);
        try {
            const link = await getAssignmentShareLink(selectedMunicipalityId);
            const url = `${window.location.origin}/uppdrag?t=${link.token}`;
            try {
                await navigator.clipboard.writeText(url);
                toast.success("Länk kopierad – skicka den till utdelarna.");
            } catch {
                // Urklipp kan vara blockerat (t.ex. utan https) — visa länken så den går att kopiera för hand.
                window.prompt("Kopiera länken:", url);
            }
        } catch {
            toast.error("Kunde inte hämta utdelarlänken.");
        } finally {
            setIsCopyingLink(false);
        }
    };

    return (
        <div className="assignments-table-page">
            <div className="assignments-table-page__toolbar">
                <div className="assignments-table-page__toolbar-left">
                    <Link to="/admin/assignments" className="assignments-table-page__back-link">
                        ← Uppdrag på karta
                    </Link>
                    <label className="assignments-table-page__municipality">
                        Kommun
                        <select
                            value={selectedMunicipalityId}
                            onChange={(event) => setSelectedMunicipalityId(event.target.value)}
                        >
                            <option value="">Välj kommun</option>
                            {municipalities.map((municipality) => (
                                <option key={municipality.id} value={municipality.id}>
                                    {municipality.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <div className="assignments-table-page__group-by">
                        Sortera på
                        <div className="assignments-table-page__segmented">
                            {(["area", "distributor"] as const).map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={groupBy === value ? "is-active" : ""}
                                    onClick={() => {
                                        setGroupBy(value);
                                        storeAssignmentSortBy(value);
                                    }}
                                >
                                    {ASSIGNMENT_SORT_LABELS[value]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="assignments-table-page__toolbar-actions">
                    <button
                        type="button"
                        className="assignments-table-page__share-button"
                        onClick={handleCopyShareLink}
                        disabled={!selectedMunicipalityId || isCopyingLink}
                        title="Öppen länk (utan inloggning) där utdelarna svarar på utdelningarna"
                    >
                        {isCopyingLink ? "Hämtar länk…" : "Kopiera utdelarlänk"}
                    </button>
                    <button
                        type="button"
                        className="assignments-table-page__share-button"
                        onClick={() => setIsRoundsDialogOpen(true)}
                        disabled={!selectedMunicipalityId || createRoundsMutation.isPending}
                        title="Starta en eller flera utdelningar – utdelarna svarar på alla öppna på en gång"
                    >
                        Ny utdelning
                    </button>
                    <button
                        type="button"
                        className="assignments-table-page__print-button"
                        onClick={() => window.print()}
                        disabled={!selectedMunicipalityId || assignments.length === 0}
                    >
                        Skriv ut
                    </button>
                </div>
            </div>

            <h1 className="assignments-table-page__title">
                Utdelningslista{municipalityName ? ` – ${municipalityName}` : ""}
            </h1>

            {selectedMunicipalityId && (
                <div className="assignments-table-page__rounds">
                    {openRounds.length === 0 ? (
                        <p className="assignments-table-page__round">
                            Ingen utdelning startad – starta en för att kunna samla in svar.
                        </p>
                    ) : (
                        openRounds.map((round) => (
                            <p key={round.id} className="assignments-table-page__round">
                                <strong>{round.name}</strong> · {round.yesCount} ja · {round.noCount} nej ·{" "}
                                {round.assignmentMaterialReceivedCount} områden har material
                                <button
                                    type="button"
                                    className="assignments-table-page__close-round"
                                    disabled={closeRoundMutation.isPending}
                                    onClick={() => handleCloseRound(round)}
                                    title="Avsluta utdelningen – svaren sparas"
                                >
                                    Avsluta
                                </button>
                            </p>
                        ))
                    )}
                </div>
            )}

            {assignments.length > 0 && (
                <ul className="assignments-table-page__legend">
                    {(["ready", "partial", "incomplete", "noAnswer", "declined", "delivered"] as const).map((status) => (
                        <li key={status}>
                            <span className={`assignments-table__dot assignments-table__dot--${status}`} />
                            {STATUS_LABELS[status]}
                        </li>
                    ))}
                </ul>
            )}

            {!selectedMunicipalityId && (
                <p className="assignments-table-page__hint">Välj kommun för att visa uppdragen.</p>
            )}
            {selectedMunicipalityId && isLoading && <p className="assignments-table-page__hint">Laddar uppdrag…</p>}
            {selectedMunicipalityId && isError && (
                <p role="alert" className="assignments-table-page__error">
                    Kunde inte hämta uppdrag.{" "}
                    <button type="button" onClick={() => refetch()}>
                        Försök igen
                    </button>
                </p>
            )}
            {selectedMunicipalityId && !isLoading && !isError && assignments.length === 0 && (
                <p className="assignments-table-page__hint">Inga uppdrag i vald kommun.</p>
            )}

            {assignments.length > 0 &&
                sections.map((section) => (
                    <section
                        key={section.key}
                        className={`assignments-table-page__section${
                            printSectionKey && printSectionKey !== section.key
                                ? " assignments-table-page__section--print-hidden"
                                : ""
                        }`}
                    >
                        <div className="assignments-table-page__section-header">
                            <h2 className="assignments-table-page__section-title">
                                {section.title}{" "}
                                <span className="assignments-table-page__section-count">
                                    ({section.rows.length})
                                </span>
                            </h2>
                            {section.rows.length > 0 && (
                                <button
                                    type="button"
                                    className="assignments-table-page__section-export assignments-table__screen-only"
                                    onClick={() => setPrintSectionKey(section.key)}
                                    title="Skriv ut bara den här gruppens lista"
                                >
                                    Skriv ut lista
                                </button>
                            )}
                            {section.rows.some((row) => row.assignment.area) && (
                                <button
                                    type="button"
                                    className="assignments-table-page__section-export assignments-table__screen-only"
                                    disabled={exportingSectionKey !== null}
                                    onClick={() => handleExportSectionMaps(section)}
                                    title="En PDF med alla kartor i gruppen – en A4-sida per uppdrag, med tom baksida så dubbelsidig utskrift ger en karta per blad"
                                >
                                    {exportingSectionKey === section.key
                                        ? "Skapar PDF…"
                                        : `Exportera kartor (${section.rows.filter((row) => row.assignment.area).length})`}
                                </button>
                            )}
                        </div>

                        {section.rows.length === 0 ? (
                            <p className="assignments-table-page__hint">{section.empty}</p>
                        ) : (
                            <table className="assignments-table">
                                <thead>
                                    <tr>
                                        <th>Namn</th>
                                        <th className="assignments-table__num">Antal</th>
                                        <th>Adress</th>
                                        <th>Telefon</th>
                                        <th>Materialval</th>
                                        {openRounds.map((round) => (
                                            <th key={round.id}>{round.name}</th>
                                        ))}
                                        <th className="assignments-table__screen-only-cell" />
                                    </tr>
                                </thead>
                                <tbody>{section.rows.map(renderRow)}</tbody>
                                <tfoot>
                                    <tr>
                                        <td>Totalt ({section.rows.length} uppdrag)</td>
                                        <td className="assignments-table__num">
                                            {section.totalTargetAddresses}
                                        </td>
                                        <td colSpan={3 + openRounds.length} />
                                        <td className="assignments-table__screen-only-cell" />
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </section>
                ))}

            {isRoundsDialogOpen && (
                <DistributionRoundsDialog
                    municipalityName={municipalityName}
                    openRounds={openRounds}
                    initialNames={suggestRoundNames()}
                    isPending={createRoundsMutation.isPending}
                    onCancel={() => setIsRoundsDialogOpen(false)}
                    onSubmit={handleCreateRounds}
                />
            )}

            {mapAssignment && (
                <AssignmentMapModal
                    assignment={mapAssignment}
                    onClose={() => setMapAssignment(null)}
                    onDownload={() => handleDownloadMap(mapAssignment)}
                    isDownloading={exportingId === mapAssignment.id}
                />
            )}
        </div>
    );
};

export default AdminAssignmentsTablePage;
